import { useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

interface DecisionItem {
  id: string;
  title: string;
  filed: string;
  status: string;
  category?: string;
  source_memo?: string;
}

interface TaskItem {
  id: string;
  title: string;
  status: string;
  tags?: string[];
  priority: string;
  category?: string;
  created_at?: string;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function PendingDecisionsWidget({ context }: WidgetProps) {
  const [decisions, setDecisions] = useState<DecisionItem[] | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    if (!context.isDirector) return;

    // Try to get tasks tagged with decision-required or blocked status
    void engineFetchJson<TaskItem[]>('/tasks/?status=blocked&limit=20')
      .then((tasks) => {
        // Filter for decision-related tasks
        const decisionTasks = tasks.filter(
          (t) =>
            t.tags?.includes('decision-required') ||
            t.tags?.includes('brainstorm-required') ||
            t.category === 'DECISIONS' ||
            t.title.toLowerCase().includes('decision') ||
            t.title.toLowerCase().includes('ruling'),
        );

        // If no decision-specific tasks, show all blocked tasks as decisions needing Director
        const items = (decisionTasks.length > 0 ? decisionTasks : tasks.slice(0, 10)).map((t) => ({
          id: t.id,
          title: t.title,
          filed: t.created_at ?? new Date().toISOString(),
          status: t.status,
          category: t.category,
        }));

        setDecisions(items);
      })
      .catch(() => {
        setEndpointMissing(true);
      });
  }, [context.authToken, context.isDirector]);

  return (
    <div className="sp-widget">
      <div className="sp-widget-header">
        <span>Pending Decisions</span>
        <a href="/board" style={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
          Full board
        </a>
      </div>
      <div className="sp-widget-body" style={{ padding: '4px 12px' }}>
        {endpointMissing ? (
          <div>
            <div style={{ color: 'var(--sp-warn)', fontSize: 12, marginBottom: 4 }}>
              Awaiting engine endpoint
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 2 }}>
              GET /api/v1/tasks/?status=blocked
            </div>
            <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, marginBottom: 8 }}>
              (or GET /api/v1/decisions/pending)
            </div>
            {/* Mock decision layout */}
            <div style={{ opacity: 0.35 }}>
              {[
                'Rust sidecar vs PyInstaller for desktop packaging',
                'Canon calibration: per-model or shared?',
                'Embedded/OEM pricing tier structure',
              ].map((d, i) => (
                <div key={i} style={{ padding: '5px 0', fontSize: 12, borderBottom: '1px solid var(--sp-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--sp-warn)', fontSize: 14, lineHeight: 1 }}>?</span>
                    <span style={{ color: 'var(--sp-text-muted)' }}>{d}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : decisions ? (
          decisions.length === 0 ? (
            <div style={{ color: 'var(--sp-text-muted)', fontSize: 12, padding: '8px 0' }}>
              No pending decisions. All clear.
            </div>
          ) : (
            <div style={{ maxHeight: 240, overflow: 'auto' }}>
              <div style={{ fontSize: 11, color: 'var(--sp-text-dim)', marginBottom: 4 }}>
                {decisions.length} item{decisions.length === 1 ? '' : 's'} awaiting Director ruling
              </div>
              {decisions.map((d) => (
                <div
                  key={d.id}
                  style={{
                    padding: '5px 0',
                    fontSize: 12,
                    borderBottom: '1px solid var(--sp-border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ color: 'var(--sp-warn)', fontSize: 14, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>?</span>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.title}>
                        {d.title}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                        {d.category && (
                          <span className="mono" style={{ fontSize: 9, color: 'var(--sp-text-dim)' }}>{d.category}</span>
                        )}
                        <span className="mono" style={{ fontSize: 9, color: 'var(--sp-text-dim)' }}>{timeAgo(d.filed)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div style={{ color: 'var(--sp-text-muted)', fontSize: 12 }}>Loading decisions...</div>
        )}
      </div>
    </div>
  );
}

export const pendingDecisionsWidget: WidgetDefinition = {
  id: 'pending-decisions',
  name: 'Pending Decisions',
  description: 'Director decisions and blocked items awaiting ruling.',
  icon: 'gavel',
  category: 'admin',
  scope: 'director',
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  component: PendingDecisionsWidget,
};
